import {
  AuditReportsPublicJobDto,
  AuditReportsPublicJobDtoStatusEnum,
  AuditReportsPublicJobDtoStatusEnumKey,
  VideoEditorPublicJobWithResultDto,
} from '@mi-front/api-v2';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Chip, CircularProgress } from '@mui/material';
import { ChipProps } from '@mui/material';
import { CSSProperties, ReactElement, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { CircularProgressWithLabel } from './CircularProgressWithLabel';

/** Optional labels keyed by job status to override default status chip labels */
export type JobProgressStatusLabels = Partial<
  Record<AuditReportsPublicJobDtoStatusEnumKey, ReactNode>
>;

/** Optional chip icons keyed by job status. In-progress spinner icons take precedence. */
export type JobProgressStatusIcons = Partial<
  Record<AuditReportsPublicJobDtoStatusEnumKey, ReactElement>
>;

/** Optional chip colors keyed by job status. Only provided statuses are overridden. */
export type JobProgressStatusColors = Partial<
  Record<AuditReportsPublicJobDtoStatusEnumKey, ChipProps['color']>
>;

interface JobProgressProps {
  // FIXME
  job: AuditReportsPublicJobDto | VideoEditorPublicJobWithResultDto;
  size?: 'small' | 'medium';
  showStatus?: boolean;
  showOnlyProgress?: boolean;
  variant?: 'outlined' | 'filled';
  /** Chip `sx.background` when `variant` is `outlined`. Ignored for `filled`. */
  background?: CSSProperties['background'];
  /** Override default status labels by status. Only provided statuses are overridden. */
  statusLabels?: JobProgressStatusLabels;
  /** Override default chip icons by status. Ignored for in-progress statuses that show a spinner. */
  statusIcons?: JobProgressStatusIcons;
  /** Override default chip colors by status. Only provided statuses are overridden. */
  statusColors?: JobProgressStatusColors;
}

export function JobProgress({
  job,
  size = 'small',
  showStatus = true,
  showOnlyProgress = false,
  variant = 'outlined',
  background,
  statusLabels,
  statusIcons,
  statusColors,
}: JobProgressProps) {
  const { $t } = useIntl();
  const { status, progressPercent } = job;

  /** Primary in-progress statuses (shown as "En curso" / "En progreso" chips). */
  const isInProgressLike =
    status === AuditReportsPublicJobDtoStatusEnum.IN_PROGRESS ||
    status === AuditReportsPublicJobDtoStatusEnum.WAITING_CHILDREN;

  /**
   * Determinate ring only when IN_PROGRESS reports a meaningful percent.
   * `0` is treated as "no progress yet" so we show an indeterminate spinner instead
   * of an empty determinate ring that looks like a missing icon.
   */
  const hasProgressPercent =
    status === AuditReportsPublicJobDtoStatusEnum.IN_PROGRESS &&
    typeof progressPercent === 'number' &&
    progressPercent > 0;

  const progress = hasProgressPercent ? progressPercent : 0;

  // Get status color and label; use statusLabels override when provided
  const getStatusInfo = (status: AuditReportsPublicJobDtoStatusEnumKey) => {
    const overrideLabel = statusLabels?.[status];
    const base = (() => {
      switch (status) {
        case AuditReportsPublicJobDtoStatusEnum.SCHEDULED:
          return {
            color: 'info' as const,
            label: $t({
              id: 'common.jobStatus.SCHEDULED',
              defaultMessage: 'Programado',
            }),
          };
        case AuditReportsPublicJobDtoStatusEnum.WAITING:
          return {
            color: 'default' as const,
            label: $t({
              id: 'common.jobStatus.WAITING',
              defaultMessage: 'En cola',
            }),
          };
        case AuditReportsPublicJobDtoStatusEnum.WAITING_CHILDREN:
        case AuditReportsPublicJobDtoStatusEnum.IN_PROGRESS:
          return {
            color: 'primary' as const,
            label: $t({
              id: 'common.jobStatus.IN_PROGRESS',
              defaultMessage: 'En progreso',
            }),
          };
        case AuditReportsPublicJobDtoStatusEnum.COMPLETED:
          return {
            color: 'success' as const,
            label: $t({
              id: 'common.jobStatus.COMPLETED',
              defaultMessage: 'Completado',
            }),
          };
        case AuditReportsPublicJobDtoStatusEnum.FAILED:
          return {
            color: 'error' as const,
            label: $t({
              id: 'common.jobStatus.FAILED',
              defaultMessage: 'Error',
            }),
          };
        case AuditReportsPublicJobDtoStatusEnum.CANCELLED:
          return {
            color: 'warning' as const,
            label: $t({
              id: 'common.jobStatus.CANCELLED',
              defaultMessage: 'Cancelado',
            }),
          };
        default:
          return { color: 'default' as const, label: status };
      }
    })();
    return {
      ...base,
      label: overrideLabel ?? base.label,
      color: statusColors?.[status] ?? base.color,
    };
  };

  const statusInfo = getStatusInfo(status);
  // Omit percent at 0; show at least 1% for any positive sub-1 progress so users see movement.
  const displayPercent = progress <= 0 ? null : progress < 1 ? 1 : Math.round(progress);
  const progressChipIconSize = size === 'small' ? 14 : 18;
  const statusIconFontSize = size === 'small' ? 'small' : 'medium';

  const defaultStatusIcons: JobProgressStatusIcons = {
    [AuditReportsPublicJobDtoStatusEnum.COMPLETED]: (
      <CheckCircleOutlinedIcon fontSize={statusIconFontSize} />
    ),
    [AuditReportsPublicJobDtoStatusEnum.FAILED]: <ErrorOutlineIcon fontSize={statusIconFontSize} />,
  };

  const chipLabel =
    hasProgressPercent && showStatus && !showOnlyProgress ? (
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, typography: 'inherit' }}
      >
        <Box component="span">{statusInfo.label}</Box>
        {displayPercent !== null && <Box component="span">{`(${displayPercent}%)`}</Box>}
      </Box>
    ) : (
      statusInfo.label
    );

  const showProgressOutsideChip = hasProgressPercent && (!showStatus || showOnlyProgress);

  /** Spinner for in-progress chips: determinate when percent is known, otherwise indeterminate. */
  const progressChipIcon = isInProgressLike ? (
    <CircularProgress
      variant={hasProgressPercent ? 'determinate' : 'indeterminate'}
      value={hasProgressPercent ? progress : undefined}
      size={progressChipIconSize}
      thickness={4}
    />
  ) : undefined;

  const chipIcon = progressChipIcon ?? statusIcons?.[status] ?? defaultStatusIcons[status];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {showStatus && !showOnlyProgress && (
        <Chip
          {...(chipIcon ? { icon: chipIcon } : {})}
          label={chipLabel}
          color={statusInfo.color}
          size={size === 'small' ? 'small' : 'medium'}
          variant={variant}
          sx={{
            ...(variant === 'outlined' && background !== undefined ? { background } : {}),
            ...(chipIcon ? { pl: 0.5 } : {}),
          }}
        />
      )}
      {showProgressOutsideChip && (
        <CircularProgressWithLabel value={progress} size={size === 'small' ? 28 : 44} />
      )}
    </Box>
  );
}
